import { Grid, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
//
import AlertDialog from '../../componets/dialog/alertDialog';
import { FormButton } from '../../componets/form/formButton';
import { paths } from '../../config';
import { useContextGlobal } from '../../context/ContextGlobal';
import { useToast } from '../../context/ToastContext';
import api from '../../services/api';
import {
    handleExceptionMessage,
    handleExceptionMultipleMessages,
} from '../../util/handleExceptionAxios';
import { message } from '../../util/handleMessages';
import { FormInputProps } from '../../componets/form/formInterfaces';
import RenderForm from '../../componets/form/renderForm';
import IDescription from '../../util/interfaces/description';
import { IconComponent, icons } from '../../componets/icons';

interface IFormInput {
    description: string;
    rights: [];
    company: {
        id: string;
        description: string;
    };
}

interface IUpdateAutocomplete {
    id: string;
    keyField: 'right';
}

const defaultValues = {};

const settings = {
    pathRegister: paths.roleRegister,
    pathList: paths.role,
    apiPath: 'role',
    roleAdmin: null,
    title: 'Papel',
};

const Form: React.FC = () => {
    const history = useHistory();
    const params = useParams<'id' | any>();
    const { addToast } = useToast();
    const { setOpenLoading } = useContextGlobal();
    const [openModalDelete, setOpenModalDelete] = useState(false);
    const [optionsRight, setOptionsRight] = useState<IDescription[]>([]);
    const [optionsCompany, setOptionsCompany] = useState([]);
    const [loadingAutocomplete, setLoadingAutocomplete] = useState(false);

    const { handleSubmit, control, reset, setValue } = useForm<IFormInput>({
        defaultValues,
    });

    useEffect(() => {
        // Caso seja a alteração de um registo, neste caso precisava vir todas as rights
        async function loadRights(companyId: string) {
            const response = await api.get(
                `right?limit=50&company.id=${companyId}`,
            );
            const data = response.data.data.map((d: any) => {
                return {
                    id: d.id,
                    description: d.description,
                };
            });
            setOptionsRight(data);
        }
        if (params && params.id) {
            setOpenLoading(true);

            api.get(`${settings.apiPath}/${params.id}`)
                .then(response => {
                    setModel(response.data);
                    loadRights(response.data.company.id);
                    setOpenLoading(false);
                })
                .catch(e => {
                    console.error(e);
                    setOpenLoading(false);
                    addToast({
                        type: 'error',
                        title: message.error.selectOne,
                    });
                });
        }

        api.get(`company`)
            .then(response => {
                setOptionsCompany(response.data.data);
            })
            .catch(e => {
                console.error(e);
                addToast({
                    type: 'error',
                    title: message.error.selectAll,
                });
            });
    }, []);

    const autocompleteSettings = {
        right: {
            query: (value: string) => `right?company.id=${value}`,
            setOptions: (options: []) => {
                const data = options.map((d: any) => {
                    return {
                        id: d.id,
                        description: d.description,
                    };
                });
                setOptionsRight(data);
            },
        },
    };

    const updateAutocomplete = async ({
        id,
        keyField,
    }: IUpdateAutocomplete) => {
        if (id && id.length > 0) {
            setLoadingAutocomplete(true);
            const response = await api.get(
                autocompleteSettings[keyField].query(id),
            );
            autocompleteSettings[keyField].setOptions(response.data.data);
            setLoadingAutocomplete(false);
        }
    };

    const onChangeSetValues = (
        event: React.ChangeEvent<HTMLInputElement>,
        option: any,
    ) => {
        if (option) {
            let keyField: 'right' = 'right';

            updateAutocomplete({ id: option.id, keyField });
        }
    };

    const inputsForm: FormInputProps<IFormInput>[] = [
        {
            typeInput: 'text',
            name: 'description',
            control: control,
            label: 'Descrição',
            md: 6,
            xs: 12,
            rules: {
                required: true,
            },
            messagesError: [
                {
                    type: 'required',
                    message: 'O campo descrição é obrigatório',
                },
            ],
        },
        {
            typeInput: 'autocomplete',
            name: 'company',
            control: control,
            label: 'Empresa',
            options: optionsCompany,
            setValue: setValue,
            loadingAutocomplete: loadingAutocomplete,
            md: 6,
            xs: 12,
            rules: {
                required: true,
            },
            messagesError: [
                {
                    type: 'required',
                    message: 'O campo empresa é obrigatório',
                },
            ],
            handleChange: onChangeSetValues,
            activeDebounce: true,
        },
        {
            typeInput: 'autocomplete',
            name: 'rights',
            control: control,
            label: 'Direitos',
            options: optionsRight,
            setValue: setValue,
            loadingAutocomplete: loadingAutocomplete,
            multiple: true,
            md: 12,
            xs: 12,
            rules: {
                required: true,
            },
            messagesError: [
                {
                    type: 'required',
                    message: 'O campo permissão é obrigatório',
                },
            ],
        },
    ];

    const setModel = (data: any) => {
        setValue('description', data.description);
        setValue('company', data.company);
        const rights = data.rights.map((d: any) => {
            return {
                id: d.id,
                description: d.permission.description,
            };
        });
        setValue('rights', rights);
    };

    const submit = async (data: any, e: any) => {
        e.preventDefault();
        try {
            setOpenLoading(true);

            for (const input of inputsForm) {
                if (!input?.dto) {
                    continue;
                }
                data = input.dto(data);
            }

            if (params && params.id) {
                await api.patch(`${settings.apiPath}/${params.id}`, data);
            } else {
                await api.post(`${settings.apiPath}`, data);
            }
            addToast({
                type: 'success',
                title: message.success.save,
                description: '',
            });
            setOpenLoading(false);
            reset(defaultValues);
            history.push(`${settings.pathList}?`);
        } catch (error) {
            setOpenLoading(false);
            const messagesResponse = handleExceptionMultipleMessages(error);
            for (const messageResponse of messagesResponse) {
                addToast({
                    type: 'error',
                    title: message.error.save,
                    description: messageResponse,
                });
            }
        }
    };

    const handleCancel = () => {
        history.goBack();
    };

    const handleDelete = async (id: string) => {
        setOpenLoading(true);

        try {
            await api.delete(`${settings.apiPath}/${id}`);
            addToast({
                type: 'success',
                title: message.success.delete,
                description: '',
            });
            setOpenLoading(false);
            history.push(`${settings.pathList}?`);
        } catch (error: any) {
            const messageResponse = handleExceptionMessage(error);
            setOpenLoading(false);
            addToast({
                type: 'error',
                title: message.error.delete,
                description: messageResponse,
            });
        }
    };

    const handleConfirmeDelete = async (confirm: boolean) => {
        if (confirm) {
            setOpenModalDelete(false);
            handleDelete(params.id);
        } else {
            setOpenModalDelete(false);
        }
    };

    return (
        <div className="principal-container">
            <AlertDialog
                handleConfirmation={handleConfirmeDelete}
                open={openModalDelete}
            />
            <Paper component={'div'} sx={{ pt: 2, pl: 2, pb: 2, pr: 2 }}>
                <div className="principal-container-form-header">
                    <div className="principal-container-form-header-title">
                        <Typography variant="h6" id="tableTitle" component="h1">
                            {settings.title}
                        </Typography>
                    </div>
                    <div>
                        {params.id && (
                            <Tooltip title={'Excluir'}>
                                <IconButton
                                    onClick={() => {
                                        setOpenModalDelete(true);
                                    }}>
                                    <IconComponent
                                        icon={icons.Delete}
                                        color="error"
                                    />
                                </IconButton>
                            </Tooltip>
                        )}
                    </div>
                </div>
                <form onSubmit={handleSubmit(submit)}>
                    <Grid container spacing={2}>
                        <RenderForm inputsForm={inputsForm} />
                    </Grid>
                    <br />

                    <div className="info-footer">
                        <div className="footer-buttons">
                            <FormButton
                                label={'Salvar'}
                                typeButton={'submit'}
                            />
                            <FormButton
                                label={'Voltar'}
                                typeButton={'cancel'}
                                onClick={() => handleCancel()}
                            />
                        </div>
                    </div>
                </form>
            </Paper>
        </div>
    );
};

export default Form;
